import { useMutation, useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { logValidationError } from '@/helpers/datadog';
import type { MicroSite, UpdateMicroSiteInput } from '@/types';
import { useCompanyStore } from './company';

const microSiteFragment = gql`
  fragment microSite on MicroSite {
    baseColor
    customDomain
    gaId
    headerImage
    headerSubtitle
    headerTitle
    id
    impressum
    introText
    metaDescription
    metaTitle
    showVoucherWidget
    url
  }
`;

export const useMicroSiteStore = defineStore('microSite', () => {
  const { companySettings } = storeToRefs(useCompanyStore());

  const microSite = ref<MicroSite | null>(null);
  const { onResult, loading } = useQuery(gql`
    query getMicroSite {
      microSite {
        ...microSite
      }
    }
    ${microSiteFragment}
  `);

  onResult(({ data }: { data: { microSite: MicroSite } }) => {
    if (!data) return;
    microSite.value = data.microSite;
  });

  const microSiteUrl = computed(() => microSite.value?.url || '');

  const bookingWidgetUrl = computed(() =>
    microSiteUrl.value ? `${microSiteUrl.value}/widget_bookings/new` : '',
  );

  const giftcardWidgetUrl = computed(() =>
    microSiteUrl.value ? `${microSiteUrl.value}/widget_giftcards/new` : '',
  );

  const servicesWidgetUrl = computed(() =>
    microSiteUrl.value ? `${microSiteUrl.value}/services?layout=embed` : '',
  );

  const bookingWidgetColor = computed(
    () =>
      companySettings.value.bookingWidget.customColor ||
      microSite.value?.baseColor ||
      '',
  );

  const updateMicroSite = (input: UpdateMicroSiteInput) => {
    const { mutate } = useMutation(gql`
      mutation updateMicroSite($input: UpdateMicroSiteInput!) {
        updateMicroSite(input: $input) {
          microSite {
            ...microSite
          }
          errors {
            attribute
            message
            type
          }
        }
      }
      ${microSiteFragment}
    `);
    return mutate({ input }).then(
      ({
        data: {
          updateMicroSite: { microSite: updatedMicroSite, errors },
        },
      }) => {
        if (errors) {
          logValidationError('updateMicroSite', errors);
        } else {
          microSite.value = updatedMicroSite;
        }
      },
    );
  };

  return {
    loading,
    microSite,
    microSiteUrl,
    bookingWidgetUrl,
    giftcardWidgetUrl,
    servicesWidgetUrl,
    bookingWidgetColor,
    updateMicroSite,
  };
});
